import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { toInstantOnDay } from '../../services/attendance-rules';
import { BreakTimeDetail } from '../../types/attendance.types';

interface BreakRow {
  start: string;
  end: string;
}

const EMPTY_ROW: BreakRow = { start: '', end: '' };

export interface BreakEntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The day's clock-in, so an "HH:mm" entry becomes the same instant a live Take Break/End Break
   * click would have stored; a time before it is after midnight of a night shift. */
  day: Date;
  /** The clock-out rules for the entered breaks; returns a translation key when they fail. */
  validate: (breaks: BreakTimeDetail[]) => string | null;
  onConfirm: (breaks: BreakTimeDetail[]) => void;
  onSkip: () => void;
}

/**
 * Asked only once, right before clock-out, when the employee holds ATTENDANCE.TAKE_BREAK but
 * hasn't logged any break today (e.g. they forgot to click Take Break/End Break live). Lets them
 * retroactively enter the break times they actually took instead of the day recording zero break
 * time. The entries are checked with the clock-out rules (`validate`) before the dialog closes, so
 * a problem is shown here rather than as a failed clock-out.
 */
export const BreakEntryDialog = ({
  open,
  onOpenChange,
  day,
  validate,
  onConfirm,
  onSkip,
}: BreakEntryDialogProps) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<BreakRow[]>([EMPTY_ROW]);
  const [error, setError] = useState<string | null>(null);

  const resetAndClose = () => {
    setRows([EMPTY_ROW]);
    setError(null);
    onOpenChange(false);
  };

  const updateRow = (index: number, field: keyof BreakRow, value: string) => {
    setError(null);
    setRows((current) => current.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addRow = () => setRows((current) => [...current, EMPTY_ROW]);
  const removeRow = (index: number) => setRows((current) => current.filter((_, i) => i !== index));

  const toIso = (time: string): string | null => {
    const match = /^(\d{2}):(\d{2})$/.exec(time);
    if (!match) return null;
    return toInstantOnDay(time, day, day.toISOString());
  };

  const handleConfirm = () => {
    const filledRows = rows.filter((row) => row.start || row.end);
    const breaks: BreakTimeDetail[] = [];
    for (const row of filledRows) {
      const start = toIso(row.start);
      const end = toIso(row.end);
      if (!start || !end) {
        setError('INVALID_BREAK_TIME_EACH_MIN_15_MINUTES_WITHIN_CLOCK_IN_OUT');
        return;
      }
      breaks.push({ Type: 'Regular', StartTimeUtc: start, EndTimeUtc: end });
    }
    const invalid = validate(breaks);
    if (invalid) {
      setError(invalid);
      return;
    }
    resetAndClose();
    if (breaks.length > 0) onConfirm(breaks);
    else onSkip();
  };

  // Skipping means "no breaks", which a minimum-break rule may not allow for the hours worked.
  const handleSkip = () => {
    const invalid = validate([]);
    if (invalid) {
      setError(invalid);
      return;
    }
    resetAndClose();
    onSkip();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(next) : resetAndClose())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('LOG_MISSED_BREAKS')}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">{t('LOG_MISSED_BREAKS_DESCRIPTION')}</p>

        <div className="flex flex-col gap-3">
          {rows.map((row, index) => (
            <div key={index} className="flex items-end gap-2">
              <div className="flex-1 space-y-1.5">
                {index === 0 && <Label htmlFor={`break-start-${index}`}>{t('BREAK_START')}</Label>}
                <Input
                  id={`break-start-${index}`}
                  aria-label={t('BREAK_START')}
                  type="time"
                  value={row.start}
                  onChange={(event) => updateRow(index, 'start', event.target.value)}
                />
              </div>
              <div className="flex-1 space-y-1.5">
                {index === 0 && <Label htmlFor={`break-end-${index}`}>{t('BREAK_END')}</Label>}
                <Input
                  id={`break-end-${index}`}
                  aria-label={t('BREAK_END')}
                  type="time"
                  value={row.end}
                  onChange={(event) => updateRow(index, 'end', event.target.value)}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon-lg"
                onClick={() => removeRow(index)}
                disabled={rows.length === 1}
                aria-label={t('REMOVE')}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}

          <Button type="button" variant="outline" size="sm" className="self-start" onClick={addRow}>
            <Plus className="size-4" />
            {t('ADD_BREAK')}
          </Button>
        </div>

        {error && <p className="text-sm font-medium text-destructive">{t(error)}</p>}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={handleSkip}>
            {t('SKIP')}
          </Button>
          <Button type="button" onClick={handleConfirm}>
            {t('CONTINUE')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
